/**
 * Page Analytics - Dashboard d'analyse des ventes, stocks, clients
 */

import { useState, useEffect } from 'react';
import { startOfMonth, endOfMonth, format } from 'date-fns';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { api } from '../services/api';
import KPICard from '../components/analytics/KPICard';
import DateRangePicker, { DateRange } from '../components/analytics/DateRangePicker';
import ChartCard from '../components/analytics/ChartCard';
import CourbeMarges from '../components/analytics/CourbeMarges';
import { useTenant } from '../contexts/TenantContext';

// Types
interface OwnProductionSales {
  from: string;
  to: string;
  rayon: string;
  total_ca_eur: number;
  total_ca_ht_eur: number;
  total_vat_eur: number;
  shop_ca_ttc_eur: number;
  share_percent: number;
  by_rayon: { rayon: string; ca_eur: number }[];
  by_day: { day: string; nb_sales: number; ca_eur: number }[];
  by_product: {
    product_name: string;
    rayon: string;
    quantity: number;
    unit: string;
    ca_eur: number;
    ca_ht_eur: number;
  }[];
}

interface ResaleMargins {
  from: string;
  to: string;
  rayon: string;
  supplier: string;
  total_ca_ht_eur: number;
  total_cost_sold_eur: number;
  total_gross_margin_eur: number;
  total_loss_cost_eur: number;
  total_net_margin_eur: number;
  net_margin_percent: number;
  ca_ht_all_eur: number;
  coverage_percent: number;
  by_product: {
    product_name: string;
    rayon: string;
    ca_ht_eur: number;
    gross_margin_eur: number;
    loss_cost_eur: number;
    net_margin_eur: number;
    net_margin_percent: number;
  }[];
}
interface SalesOverview {
  total_ca_cents: number;
  total_ca_eur: number;
  transaction_count: number;
  average_basket_cents: number;
  average_basket_eur: number;
  margin_cents: number;
  margin_eur: number;
  margin_percent: number;
  comparison?: {
    ca_variation_percent: number;
    transaction_variation_percent: number;
    basket_variation_percent: number;
  };
}

interface SalesByPeriod {
  period: string;
  ca_cents: number;
  ca_eur: number;
  transaction_count: number;
}

interface TopProduct {
  product_id: string;
  product_name: string;
  category_name?: string;
  quantity_sold: number;
  total_ca_eur: number;
}

interface CategoryBreakdown {
  category_id: string;
  category_name: string;
  icon?: string;
  ca_eur: number;
  percent_of_total: number;
}

interface InventoryOverview {
  total_stock_value_cents: number;
  total_stock_units: number;
  low_stock_alerts: number;
  expiring_soon: number;
}

interface CustomerOverview {
  total_customers: number;
  active_customers_30d: number;
  new_customers_month: number;
  retention_rate_percent: number;
}

interface OperationalKPIs {
  today_ca_cents: number;
  today_target_cents: number;
  today_progress_percent: number;
  transactions_today: number;
  active_alerts: Array<{
    type: string;
    count: number;
    message: string;
  }>;
}

// Types e-commerce
interface EcomOverview {
  total_ca_ttc_cents: number;
  total_ca_ht_cents: number;
  total_tax_cents: number;
  order_count: number;
  average_basket_cents: number;
  cancellation_rate_percent: number;
  delivery_count: number;
  pickup_count: number;
  comparison: {
    ca_variation_percent: number;
    order_variation_percent: number;
    basket_variation_percent: number;
  };
}

interface EcomByPeriod {
  period: string;
  ca_ttc_cents: number;
  ca_ht_cents: number;
  order_count: number;
  average_basket_cents: number;
}

interface EcomTopProduct {
  product_id: string;
  product_name: string;
  category_name: string | null;
  quantity_sold: number;
  total_ca_cents: number;
  order_count: number;
}

interface EcomCategoryBreakdown {
  category_id: string;
  category_name: string;
  ca_cents: number;
  percent_of_total: number;
  order_count: number;
}

// Types pour les marges
interface MarginAnalysis {
  total_revenue_cents: number; // CA HT (base marge)
  total_revenue_ttc_cents: number;
  total_vat_cents: number;
  total_cost_cents: number; // coût des unités à coût connu
  covered_revenue_cents: number; // CA HT à coût connu
  coverage_percent: number; // part du CA HT à coût connu
  gross_margin_cents: number; // sur le périmètre couvert
  gross_margin_percent: number; // sur le CA HT couvert
  by_category: Array<{
    category_id: string;
    category_name: string;
    revenue_cents: number; // HT
    revenue_ttc_cents: number;
    covered_revenue_cents: number;
    coverage_percent: number;
    cost_cents: number;
    margin_cents: number;
    margin_percent: number;
  }>;
}

interface ProductMargin {
  product_id: string;
  product_name: string;
  category_id: string | null;
  category_name: string;
  sale_price_cents: number;
  purchase_price_cents: number;
  default_purchase_price_cents: number;
  is_own_production: boolean;
  unit_margin_cents: number | null; // null = coût d'achat inconnu
  unit_margin_percent: number | null;
  quantity_sold: number;
  total_revenue_cents: number;
  total_cost_cents: number;
  total_margin_cents: number | null; // null = aucune unité vendue à coût connu
  coverage_percent: number; // part des unités vendues à coût connu
}

// Couleurs pour le pie chart
const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

interface B2bClientMargin {
  client_name: string;
  client_vat: string;
  invoice_count: number;
  ca_ht_eur: number;
  ca_ttc_eur: number;
  cogs_eur: number;
  ca_ht_covered_eur: number;
  margin_eur: number;
  margin_percent: number;
}

interface B2bOverview {
  invoice_count: number;
  client_count: number;
  ca_ht_eur: number;
  ca_ttc_eur: number;
  cogs_eur: number;
  ca_ht_covered_eur: number;
  margin_eur: number;
  margin_percent: number;
  coverage_percent: number;
  by_client: B2bClientMargin[];
}

type Tab = 'sales' | 'vendors' | 'production' | 'resale-margins' | 'margins' | 'b2b' | 'inventory' | 'customers' | 'operational' | 'ecommerce';

export default function Analytics() {
  const { tenant } = useTenant();
  const [activeTab, setActiveTab] = useState<Tab>('sales');
  const [vendorStats, setVendorStats] = useState<any>(null);
  const [dateRange, setDateRange] = useState<DateRange>({
    from: startOfMonth(new Date()),
    to: endOfMonth(new Date()),
  });
  const [loading, setLoading] = useState(true);

  // Data states
  const [salesOverview, setSalesOverview] = useState<SalesOverview | null>(null);
  const [salesByPeriod, setSalesByPeriod] = useState<SalesByPeriod[]>([]);
  const [topProducts, setTopProducts] = useState<TopProduct[]>([]);
  const [categoryBreakdown, setCategoryBreakdown] = useState<CategoryBreakdown[]>([]);
  const [marginAnalysis, setMarginAnalysis] = useState<MarginAnalysis | null>(null);
  const [productMargins, setProductMargins] = useState<ProductMargin[]>([]);
  const [inventoryOverview, setInventoryOverview] = useState<InventoryOverview | null>(null);
  const [customerOverview, setCustomerOverview] = useState<CustomerOverview | null>(null);
  const [operationalKPIs, setOperationalKPIs] = useState<OperationalKPIs | null>(null);
  const [ecomOverview, setEcomOverview] = useState<EcomOverview | null>(null);
  const [ecomByPeriod, setEcomByPeriod] = useState<EcomByPeriod[]>([]);
  const [ecomTopProducts, setEcomTopProducts] = useState<EcomTopProduct[]>([]);
  const [ecomCategories, setEcomCategories] = useState<EcomCategoryBreakdown[]>([]);
  const [ownProduction, setOwnProduction] = useState<OwnProductionSales | null>(null);
  const [productionRayon, setProductionRayon] = useState<string>(''); // '' = tous
  const [resaleMargins, setResaleMargins] = useState<ResaleMargins | null>(null);
  const [b2bOverview, setB2bOverview] = useState<B2bOverview | null>(null);
  const [resaleRayon, setResaleRayon] = useState<string>('');
  const [resaleSupplier, setResaleSupplier] = useState<string>('');
  const [suppliers, setSuppliers] = useState<string[]>([]);

  // Charger les données en fonction de l'onglet actif
  useEffect(() => {
    loadData();
  }, [dateRange, activeTab, productionRayon, resaleRayon, resaleSupplier]);

  // Liste des fournisseurs (filtre de l'onglet Marges revente), chargée une fois.
  useEffect(() => {
    if (!tenant?.id) return;
    api.getSuppliers(tenant.id).then(setSuppliers).catch(() => setSuppliers([]));
  }, [tenant?.id]);

  const loadData = async () => {
    setLoading(true);
    const from = format(dateRange.from, 'yyyy-MM-dd');
    const to = format(dateRange.to, 'yyyy-MM-dd');

    try {
      switch (activeTab) {
        case 'sales':
          const [overview, byPeriod, top, categories] = await Promise.all([
            api.getSalesOverview(from, to),
            api.getSalesByPeriod(from, to, 'day'),
            api.getTopProducts(from, to, 10),
            api.getCategoryBreakdown(from, to),
          ]);
          setSalesOverview(overview);
          setSalesByPeriod(byPeriod);
          setTopProducts(top);
          setCategoryBreakdown(categories);
          // Canaux additionnels du bandeau « CA total tous canaux » : best-effort.
          // Ils ne doivent JAMAIS casser l'onglet Ventes (un Promise.all les rendait
          // bloquants → l'échec de getEcomOverview vidait toute la page, 31/08/2026).
          api.getB2bOverview(from, to).then(setB2bOverview).catch(() => setB2bOverview(null));
          api.getEcomOverview(from, to).then(setEcomOverview).catch(() => setEcomOverview(null));
          break;

        case 'vendors':
          setVendorStats(await api.getSalesByVendor(from, to));
          break;

        case 'production':
          const ownProd = await api.getOwnProductionSales(from, to, productionRayon || undefined);
          setOwnProduction(ownProd);
          break;

        case 'resale-margins':
          const resale = await api.getResaleMargins(
            from,
            to,
            resaleRayon || undefined,
            resaleSupplier || undefined,
          );
          setResaleMargins(resale);
          break;

        case 'margins':
          const [margins, products] = await Promise.all([
            api.getMarginAnalysis(from, to),
            api.getProductMargins(from, to),
          ]);
          setMarginAnalysis(margins);
          setProductMargins(products);
          break;

        case 'b2b':
          setB2bOverview(await api.getB2bOverview(from, to));
          break;

        case 'inventory':
          const inventory = await api.getInventoryOverview();
          setInventoryOverview(inventory);
          break;

        case 'customers':
          const customers = await api.getCustomerOverview();
          setCustomerOverview(customers);
          break;

        case 'operational':
          const operational = await api.getOperationalKPIs();
          setOperationalKPIs(operational);
          break;

        case 'ecommerce':
          const [eOverview, eByPeriod, eTop, eCats] = await Promise.all([
            api.getEcomOverview(from, to),
            api.getEcomByPeriod(from, to, 'day'),
            api.getEcomTopProducts(from, to, 10),
            api.getEcomCategoryBreakdown(from, to),
          ]);
          setEcomOverview(eOverview);
          setEcomByPeriod(eByPeriod);
          setEcomTopProducts(eTop);
          setEcomCategories(eCats);
          break;
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('fr-BE', {
      style: 'currency',
      currency: 'EUR',
    }).format(cents / 100);
  };

  const tabs = [
    { key: 'sales', label: 'Ventes', icon: '💰' },
    { key: 'vendors', label: 'Vendeurs', icon: '👤' },
    { key: 'production', label: 'Production propre', icon: '🌱' },
    { key: 'resale-margins', label: 'Marges revente', icon: '⚖️' },
    { key: 'margins', label: 'Marges', icon: '📈' },
    { key: 'b2b', label: 'B2B / Marchés', icon: '🚚' },
    { key: 'inventory', label: 'Stocks', icon: '📦' },
    { key: 'customers', label: 'Clients', icon: '👥' },
    { key: 'operational', label: 'Opérations', icon: '📊' },
    { key: 'ecommerce', label: 'E-commerce', icon: '🛍️' },
  ];

  // Fonction pour recharger les marges produits après mise à jour
  const reloadProductMargins = async () => {
    const from = format(dateRange.from, 'yyyy-MM-dd');
    const to = format(dateRange.to, 'yyyy-MM-dd');
    const products = await api.getProductMargins(from, to);
    setProductMargins(products);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Analytics</h1>
          <p className="mt-1 text-sm text-gray-500">
            Tableaux de bord et analyses
          </p>
        </div>
      </div>

      {/* Date Range Picker */}
      <DateRangePicker value={dateRange} onChange={setDateRange} />

      {/* Tabs — 9 onglets : sur téléphone ils défilent horizontalement (motif
          standard iOS), au lieu de s'écraser ou de déborder de l'écran. */}
      <div className="border-b border-gray-200 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <nav className="flex gap-3 sm:gap-4 w-max min-w-full">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as Tab)}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap shrink-0 ${
                activeTab === tab.key
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      {activeTab === 'sales' && (
        <SalesTab
          overview={salesOverview}
          byPeriod={salesByPeriod}
          topProducts={topProducts}
          categoryBreakdown={categoryBreakdown}
          b2bOverview={b2bOverview}
          ecomOverview={ecomOverview}
          loading={loading}
          formatCurrency={formatCurrency}
        />
      )}

      {activeTab === 'vendors' && (
        <VendorsTab data={vendorStats} loading={loading} formatCurrency={formatCurrency} />
      )}

      {activeTab === 'production' && (
        <OwnProductionTab
          data={ownProduction}
          loading={loading}
          formatCurrency={formatCurrency}
          rayon={productionRayon}
          onRayonChange={setProductionRayon}
        />
      )}

      {activeTab === 'resale-margins' && (
        <ResaleMarginsTab
          data={resaleMargins}
          loading={loading}
          formatCurrency={formatCurrency}
          rayon={resaleRayon}
          onRayonChange={setResaleRayon}
          supplier={resaleSupplier}
          onSupplierChange={setResaleSupplier}
          suppliers={suppliers}
        />
      )}

      {activeTab === 'margins' && (
        <MarginsTab
          analysis={marginAnalysis}
          products={productMargins}
          loading={loading}
          formatCurrency={formatCurrency}
          onRefresh={reloadProductMargins}
          from={format(dateRange.from, 'yyyy-MM-dd')}
          to={format(dateRange.to, 'yyyy-MM-dd')}
        />
      )}

      {activeTab === 'b2b' && (
        <B2BTab data={b2bOverview} loading={loading} formatCurrency={formatCurrency} />
      )}

      {activeTab === 'inventory' && (
        <InventoryTab overview={inventoryOverview} loading={loading} formatCurrency={formatCurrency} />
      )}

      {activeTab === 'customers' && (
        <CustomersTab overview={customerOverview} loading={loading} />
      )}

      {activeTab === 'operational' && (
        <OperationalTab kpis={operationalKPIs} loading={loading} formatCurrency={formatCurrency} />
      )}

      {activeTab === 'ecommerce' && (
        <EcommerceTab
          overview={ecomOverview}
          byPeriod={ecomByPeriod}
          topProducts={ecomTopProducts}
          categories={ecomCategories}
          loading={loading}
          formatCurrency={formatCurrency}
        />
      )}
    </div>
  );
}

// ============ PRODUCTION PROPRE TAB ============
/**
 * Activité par vendeur.
 *
 * Le classement se fait sur le chiffre d'affaires parce que c'est ce qu'on
 * cherche en premier, mais la colonne « jours » et le panier moyen sont là pour
 * éviter le contresens : un vendeur présent tous les jours vendra toujours plus
 * qu'un vendeur du samedi, sans être meilleur pour autant.
 */
function VendorsTab({
  data,
  loading,
  formatCurrency,
}: {
  data: any;
  loading: boolean;
  formatCurrency: (cents: number) => string;
}) {
  if (loading) return <div className="p-12 text-center text-gray-500">Chargement…</div>;
  if (!data?.vendors?.length) {
    return <div className="p-12 text-center text-gray-500">Aucune vente sur cette période.</div>;
  }

  const meilleurPanier = data.vendors.reduce(
    (best: any, v: any) => (!best || v.average_basket_cents > best.average_basket_cents ? v : best),
    null,
  );

  return (
    <div className="space-y-4">
      <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-blue-900 text-sm">
        Le chiffre d'affaires suit d'abord le <strong>temps de présence</strong>. Pour comparer
        deux vendeurs, regardez le <strong>panier moyen</strong> et le CA par jour travaillé.
        {meilleurPanier && (
          <> Meilleur panier moyen sur la période : <strong>{meilleurPanier.vendor_name}</strong> à{' '}
          {formatCurrency(meilleurPanier.average_basket_cents)}.</>
        )}
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Vendeur</th>
              <th className="text-right px-4 py-2 font-medium">CA</th>
              <th className="text-right px-4 py-2 font-medium">Part</th>
              <th className="text-right px-4 py-2 font-medium">Ventes</th>
              <th className="text-right px-4 py-2 font-medium">Panier moyen</th>
              <th className="text-right px-4 py-2 font-medium">Jours</th>
              <th className="text-right px-4 py-2 font-medium">CA / jour</th>
              <th className="text-right px-4 py-2 font-medium">Articles / vente</th>
              <th className="text-right px-4 py-2 font-medium">Remb.</th>
            </tr>
          </thead>
          <tbody>
            {data.vendors.map((v: any) => (
              <tr key={v.vendor_id || v.vendor_name} className="border-t border-gray-100">
                <td className="px-4 py-2 font-medium text-gray-900">{v.vendor_name}</td>
                <td className="px-4 py-2 text-right tabular-nums font-semibold">
                  {formatCurrency(v.ca_cents)}
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-gray-500">
                  {v.share_percent.toFixed(1)} %
                </td>
                <td className="px-4 py-2 text-right tabular-nums">{v.sales_count}</td>
                <td
                  className={`px-4 py-2 text-right tabular-nums ${
                    meilleurPanier && v.vendor_id === meilleurPanier.vendor_id
                      ? 'font-bold text-green-700'
                      : ''
                  }`}
                >
                  {formatCurrency(v.average_basket_cents)}
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-gray-500">{v.active_days}</td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {formatCurrency(v.ca_per_day_cents)}
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-gray-500">
                  {v.lines_per_sale.toFixed(1)}
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-gray-500">
                  {v.refunds_count > 0 ? `${v.refunds_count} · ${formatCurrency(v.refunds_cents)}` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-gray-300 bg-gray-50 font-semibold">
              <td className="px-4 py-2">Total</td>
              <td className="px-4 py-2 text-right tabular-nums">
                {formatCurrency(data.total_ca_cents)}
              </td>
              <td colSpan={7} />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function OwnProductionTab({
  data,
  loading,
  formatCurrency,
  rayon,
  onRayonChange,
}: {
  data: OwnProductionSales | null;
  loading: boolean;
  formatCurrency: (cents: number) => string;
  rayon: string;
  onRayonChange: (r: string) => void;
}) {
  const eur = (v: number) => formatCurrency(Math.round(v * 100));
  const fmtQty = (q: number, unit: string) =>
    `${q.toLocaleString('fr-BE', { maximumFractionDigits: 3 })} ${unit}`;

  // Filtre rayon (Tous / Légumes / Fruits) — recharge côté serveur via onRayonChange.
  const rayonOptions = [
    { key: '', label: 'Tous' },
    { key: 'Légumes', label: '🥬 Légumes' },
    { key: 'Fruits', label: '🍓 Fruits' },
  ];
  const filterBar = (
    <div className="flex gap-2">
      {rayonOptions.map((opt) => (
        <button
          key={opt.key}
          onClick={() => onRayonChange(opt.key)}
          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
            rayon === opt.key
              ? 'bg-green-600 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );

  if (loading && !data) {
    return (
      <div className="space-y-4">
        {filterBar}
        <div className="text-center py-12 text-gray-400">Chargement…</div>
      </div>
    );
  }
  if (!data || data.by_product.length === 0) {
    return (
      <div className="space-y-4">
        {filterBar}
        <div className="text-center py-12 text-gray-400">
          Aucune vente de production propre (Les Folies Maraîchères) sur la période
          {rayon ? ` — rayon ${rayon}` : ''}.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {filterBar}

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="CA TTC"
          value={eur(data.total_ca_eur)}
          subtitle={rayon ? `Rayon ${rayon}` : 'Toute la production propre'}
          icon="🌱"
          color="green"
          loading={loading}
        />
        <KPICard title="CA HT" value={eur(data.total_ca_ht_eur)} icon="🧾" color="blue" loading={loading} />
        <KPICard title="TVA" value={eur(data.total_vat_eur)} icon="🏛️" color="orange" loading={loading} />
        <KPICard
          title="Part du CA total"
          value={`${data.share_percent.toLocaleString('fr-BE', { maximumFractionDigits: 1 })}%`}
          subtitle={`Tous canaux : ${eur(data.shop_ca_ttc_eur)} TTC`}
          icon="📊"
          color="purple"
          loading={loading}
        />
      </div>

      {/* Répartition par rayon (contexte, toujours tous rayons) */}
      {data.by_rayon.length > 1 && (
        <div className="flex flex-wrap gap-3 text-sm">
          {data.by_rayon.map((r) => (
            <span key={r.rayon} className="px-3 py-1 rounded-full bg-gray-100 text-gray-700">
              {r.rayon} : <strong>{eur(r.ca_eur)}</strong>
            </span>
          ))}
        </div>
      )}

      {/* CA par jour */}
      <ChartCard title="CA par jour (TTC)" loading={loading}>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={data.by_day}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="day" tickFormatter={(v) => format(new Date(v), 'dd/MM')} fontSize={12} />
            <YAxis tickFormatter={(v) => `${Number(v).toFixed(0)}€`} fontSize={12} />
            <Tooltip
              formatter={(v: any) => eur(Number(v))}
              labelFormatter={(l) => format(new Date(String(l)), 'dd/MM/yyyy')}
            />
            <Bar dataKey="ca_eur" fill="#2d6a4f" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Détail par produit */}
      <ChartCard title="Détail par produit" loading={loading}>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2 pr-4">Produit</th>
                <th className="py-2 pr-4">Rayon</th>
                <th className="py-2 pr-4 text-right">Quantité</th>
                <th className="py-2 pr-4 text-right">CA HT</th>
                <th className="py-2 pr-4 text-right">CA TTC</th>
              </tr>
            </thead>
            <tbody>
              {data.by_product.map((p, i) => (
                <tr key={i} className="border-b last:border-0">
                  <td className="py-2 pr-4 font-medium text-gray-900">{p.product_name}</td>
                  <td className="py-2 pr-4 text-gray-500">{p.rayon}</td>
                  <td className="py-2 pr-4 text-right tabular-nums">{fmtQty(p.quantity, p.unit)}</td>
                  <td className="py-2 pr-4 text-right tabular-nums text-gray-600">{eur(p.ca_ht_eur)}</td>
                  <td className="py-2 pr-4 text-right tabular-nums font-semibold">{eur(p.ca_eur)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>
    </div>
  );
}

// ============ MARGES REVENTE TAB ============
function ResaleMarginsTab({
  data,
  loading,
  formatCurrency,
  rayon,
  onRayonChange,
  supplier,
  onSupplierChange,
  suppliers,
}: {
  data: ResaleMargins | null;
  loading: boolean;
  formatCurrency: (cents: number) => string;
  rayon: string;
  onRayonChange: (r: string) => void;
  supplier: string;
  onSupplierChange: (s: string) => void;
  suppliers: string[];
}) {
  const eur = (v: number) => formatCurrency(Math.round(v * 100));
  const pct = (v: number) => `${v.toLocaleString('fr-BE', { maximumFractionDigits: 1 })}%`;

  const rayonOptions = [
    { key: '', label: 'Tous rayons' },
    { key: 'Fruits', label: '🍓 Fruits' },
    { key: 'Légumes', label: '🥬 Légumes' },
  ];
  const filterBar = (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex gap-2">
        {rayonOptions.map((opt) => (
          <button
            key={opt.key}
            onClick={() => onRayonChange(opt.key)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              rayon === opt.key ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
      <select
        value={supplier}
        onChange={(e) => onSupplierChange(e.target.value)}
        className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm bg-white"
      >
        <option value="">Tous fournisseurs</option>
        {suppliers.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>
    </div>
  );

  if (loading && !data) {
    return (
      <div className="space-y-4">
        {filterBar}
        <div className="text-center py-12 text-gray-400">Chargement…</div>
      </div>
    );
  }
  if (!data || data.by_product.length === 0) {
    return (
      <div className="space-y-4">
        {filterBar}
        <div className="text-center py-12 text-gray-400">
          Aucune vente de revente à coût connu sur la période / le filtre choisi.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {filterBar}

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="CA HT (coût connu)"
          value={eur(data.total_ca_ht_eur)}
          subtitle={`Couverture ${pct(data.coverage_percent)}`}
          icon="🧾"
          color="blue"
          loading={loading}
        />
        <KPICard title="Marge brute" value={eur(data.total_gross_margin_eur)} icon="📈" color="green" loading={loading} />
        <KPICard
          title="Démarque (pertes)"
          value={`− ${eur(data.total_loss_cost_eur)}`}
          icon="🗑️"
          color="orange"
          loading={loading}
        />
        <KPICard
          title="Marge nette"
          value={eur(data.total_net_margin_eur)}
          subtitle={`${pct(data.net_margin_percent)} du CA HT`}
          icon="💚"
          color="purple"
          loading={loading}
        />
      </div>

      {data.coverage_percent < 100 && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          ⚠️ Marge calculée sur {pct(data.coverage_percent)} du CA revente (lots à prix d'achat renseigné). CA
          revente total, coût inconnu inclus : {eur(data.ca_ht_all_eur)} HT.
        </div>
      )}

      {/* Détail par produit */}
      <ChartCard title="Marge par produit (nette = brute − pertes du lot)" loading={loading}>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2 pr-4">Produit</th>
                <th className="py-2 pr-4">Rayon</th>
                <th className="py-2 pr-4 text-right">CA HT</th>
                <th className="py-2 pr-4 text-right">Marge brute</th>
                <th className="py-2 pr-4 text-right">Pertes</th>
                <th className="py-2 pr-4 text-right">Marge nette</th>
                <th className="py-2 pr-4 text-right">%</th>
              </tr>
            </thead>
            <tbody>
              {data.by_product.map((p, i) => (
                <tr key={i} className="border-b last:border-0">
                  <td className="py-2 pr-4 font-medium text-gray-900">{p.product_name}</td>
                  <td className="py-2 pr-4 text-gray-500">{p.rayon}</td>
                  <td className="py-2 pr-4 text-right tabular-nums">{eur(p.ca_ht_eur)}</td>
                  <td className="py-2 pr-4 text-right tabular-nums">{eur(p.gross_margin_eur)}</td>
                  <td className="py-2 pr-4 text-right tabular-nums text-amber-600">
                    {p.loss_cost_eur > 0 ? `− ${eur(p.loss_cost_eur)}` : '—'}
                  </td>
                  <td className="py-2 pr-4 text-right tabular-nums font-semibold">{eur(p.net_margin_eur)}</td>
                  <td
                    className={`py-2 pr-4 text-right tabular-nums ${
                      p.net_margin_percent < 0 ? 'text-red-600' : 'text-gray-600'
                    }`}
                  >
                    {pct(p.net_margin_percent)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>
    </div>
  );
}

// ============ B2B / MARCHÉS TAB ============
function B2BTab({
  data,
  loading,
  formatCurrency,
}: {
  data: B2bOverview | null;
  loading: boolean;
  formatCurrency: (cents: number) => string;
}) {
  const eur = (v: number) => formatCurrency(Math.round(v * 100));
  const pct = (v: number) => `${v.toLocaleString('fr-BE', { maximumFractionDigits: 1 })}%`;

  if (loading && !data) {
    return <div className="text-center py-12 text-gray-400">Chargement…</div>;
  }
  if (!data || data.invoice_count === 0) {
    return (
      <div className="text-center py-12 text-gray-400">
        Aucune facture B2B (Enfact importée) sur la période choisie.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
        Ventes B2B facturées via Enfact (marchés publics / gros) : toutes les lignes des factures, hors vidanges
        (caisses EPS). Ce chiffre n'est <strong>pas</strong> compté dans le CA global (onglet Ventes), qui ne couvre que la caisse.
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="CA B2B (HT)"
          value={eur(data.ca_ht_eur)}
          subtitle={`${eur(data.ca_ttc_eur)} TTC · ${data.invoice_count} factures`}
          icon="🚚"
          color="blue"
          loading={loading}
        />
        <KPICard
          title="Coût marchandises"
          value={`− ${eur(data.cogs_eur)}`}
          subtitle="Prix d'achat des lots sortis"
          icon="📦"
          color="orange"
          loading={loading}
        />
        <KPICard
          title="Marge brute"
          value={eur(data.margin_eur)}
          subtitle={`${pct(data.margin_percent)} du CA à coût connu`}
          icon="📈"
          color="green"
          loading={loading}
        />
        <KPICard
          title="Clients B2B"
          value={data.client_count}
          subtitle={`Couverture coût ${pct(data.coverage_percent)}`}
          icon="🏢"
          color="purple"
          loading={loading}
        />
      </div>

      {data.coverage_percent < 100 && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          ⚠️ Marge calculée sur {pct(data.coverage_percent)} du CA B2B (lignes déduites du stock, d'un lot au prix
          d'achat renseigné). Le reste — paniers, animations, factures d'avant le suivi du stock — est facturé mais
          sans coût connu : jamais compté à coût 0.
        </div>
      )}

      {/* Détail par client */}
      <ChartCard title="CA & marge par client (marché)" loading={loading}>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2 pr-4">Client</th>
                <th className="py-2 pr-4 text-right">Factures</th>
                <th className="py-2 pr-4 text-right">CA HT</th>
                <th className="py-2 pr-4 text-right">CA TTC</th>
                <th className="py-2 pr-4 text-right">Coût</th>
                <th className="py-2 pr-4 text-right">Marge</th>
                <th className="py-2 pr-4 text-right">%</th>
              </tr>
            </thead>
            <tbody>
              {data.by_client.map((c, i) => (
                <tr key={i} className="border-b last:border-0">
                  <td className="py-2 pr-4 font-medium text-gray-900">
                    {c.client_name}
                    {c.client_vat && <span className="block text-xs text-gray-400">{c.client_vat}</span>}
                  </td>
                  <td className="py-2 pr-4 text-right tabular-nums text-gray-500">{c.invoice_count}</td>
                  <td className="py-2 pr-4 text-right tabular-nums">{eur(c.ca_ht_eur)}</td>
                  <td className="py-2 pr-4 text-right tabular-nums text-gray-500">{eur(c.ca_ttc_eur)}</td>
                  <td className="py-2 pr-4 text-right tabular-nums text-amber-600">
                    {c.cogs_eur > 0 ? `− ${eur(c.cogs_eur)}` : '—'}
                  </td>
                  <td className="py-2 pr-4 text-right tabular-nums font-semibold">{eur(c.margin_eur)}</td>
                  <td
                    className={`py-2 pr-4 text-right tabular-nums ${
                      c.margin_percent < 0 ? 'text-red-600' : 'text-gray-600'
                    }`}
                  >
                    {c.ca_ht_covered_eur > 0 ? pct(c.margin_percent) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>
    </div>
  );
}

// ============ SALES TAB ============
function SalesTab({
  overview,
  byPeriod,
  topProducts,
  categoryBreakdown,
  b2bOverview,
  ecomOverview,
  loading,
  formatCurrency,
}: {
  overview: SalesOverview | null;
  byPeriod: SalesByPeriod[];
  topProducts: TopProduct[];
  categoryBreakdown: CategoryBreakdown[];
  b2bOverview?: B2bOverview | null;
  ecomOverview?: EcomOverview | null;
  loading: boolean;
  formatCurrency: (cents: number) => string;
}) {
  const caisseCents = overview?.total_ca_cents ?? 0;
  const b2bCents = b2bOverview ? Math.round(b2bOverview.ca_ttc_eur * 100) : 0;
  const ecomCents = ecomOverview?.total_ca_ttc_cents ?? 0;
  const totalCents = caisseCents + b2bCents + ecomCents;

  return (
    <div className="space-y-6">
      {/* CA total tous canaux (Caisse + B2B + E-commerce) — le CA global de l'onglet
          Ventes ne compte que la caisse ; ce bandeau ajoute les factures Enfact B2B
          (absentes de la table sale) et l'e-commerce, sans mélanger les colonnes. */}
      {totalCents > 0 && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-5 py-4">
          <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2">
            <div>
              <div className="text-sm font-medium text-emerald-800">CA total tous canaux (TTC)</div>
              <div className="text-3xl font-bold text-emerald-900 tabular-nums">
                {formatCurrency(totalCents)}
              </div>
            </div>
            <div className="text-sm text-emerald-800/90 tabular-nums">
              Caisse {formatCurrency(caisseCents)}
              {b2bOverview && <> · B2B {formatCurrency(b2bCents)}</>}
              {ecomOverview && <> · E-commerce {formatCurrency(ecomCents)}</>}
            </div>
          </div>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Chiffre d'affaires (TTC)"
          value={overview ? formatCurrency(overview.total_ca_cents) : '-'}
          trend={overview?.comparison?.ca_variation_percent}
          trendLabel="vs période précédente"
          icon="💰"
          color="blue"
          loading={loading}
        />
        <KPICard
          title="Transactions"
          value={overview?.transaction_count ?? '-'}
          trend={overview?.comparison?.transaction_variation_percent}
          trendLabel="vs période précédente"
          icon="🧾"
          color="green"
          loading={loading}
        />
        <KPICard
          title="Panier moyen"
          value={overview ? formatCurrency(overview.average_basket_cents) : '-'}
          trend={overview?.comparison?.basket_variation_percent}
          trendLabel="vs période précédente"
          icon="🛒"
          color="orange"
          loading={loading}
        />
        <KPICard
          title="Marge brute (HT)"
          value={overview ? `${overview.margin_percent}%` : '-'}
          subtitle={overview ? `${formatCurrency(overview.margin_cents)} · hors consignes` : undefined}
          icon="📈"
          color="purple"
          loading={loading}
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Evolution CA */}
        <ChartCard title="Évolution du CA" subtitle="Par jour" loading={loading}>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={byPeriod}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="period"
                tickFormatter={(value) => format(new Date(value), 'dd/MM')}
                fontSize={12}
              />
              <YAxis
                tickFormatter={(value) => `${(value / 100).toFixed(0)}€`}
                fontSize={12}
              />
              <Tooltip
                formatter={(value) => [`${(Number(value) / 100).toFixed(2)}€`, 'CA']}
                labelFormatter={(label) => format(new Date(String(label)), 'dd/MM/yyyy')}
              />
              <Line
                type="monotone"
                dataKey="ca_cents"
                stroke="#3B82F6"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Répartition par catégorie */}
        <ChartCard title="CA par catégorie" loading={loading}>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={categoryBreakdown as any[]}
                dataKey="ca_eur"
                nameKey="category_name"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label={({ name, payload }: any) =>
                  `${name} (${payload?.percent_of_total || 0}%)`
                }
                labelLine={false}
              >
                {categoryBreakdown.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => [`${Number(value).toFixed(2)}€`, 'CA']} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Top Products */}
      <ChartCard title="Top 10 Produits" subtitle="Par chiffre d'affaires" loading={loading}>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={topProducts} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" tickFormatter={(value) => `${value}€`} />
            <YAxis
              type="category"
              dataKey="product_name"
              width={150}
              fontSize={12}
              tickFormatter={(value) => value.length > 20 ? value.substring(0, 20) + '...' : value}
            />
            <Tooltip
              formatter={(value) => [`${Number(value).toFixed(2)}€`, 'CA']}
            />
            <Bar dataKey="total_ca_eur" fill="#3B82F6" />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}

// ============ MARGINS TAB ============
function MarginsTab({
  analysis,
  products,
  loading,
  formatCurrency,
  onRefresh,
  from,
  to,
}: {
  from: string;
  to: string;
  analysis: MarginAnalysis | null;
  products: ProductMargin[];
  loading: boolean;
  formatCurrency: (cents: number) => string;
  onRefresh: () => void;
}) {
  const [editingProduct, setEditingProduct] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [saving, setSaving] = useState(false);

  const handleEditStart = (product: ProductMargin) => {
    setEditingProduct(product.product_id);
    setEditValue((product.purchase_price_cents / 100).toFixed(2));
  };

  const handleEditSave = async (productId: string) => {
    const newPriceCents = Math.round(parseFloat(editValue) * 100);
    if (isNaN(newPriceCents) || newPriceCents < 0) {
      alert('Prix invalide');
      return;
    }

    setSaving(true);
    try {
      await api.updateProductPurchasePrice(productId, newPriceCents);
      setEditingProduct(null);
      onRefresh();
    } catch (error) {
      console.error('Error updating price:', error);
      alert('Erreur lors de la mise à jour du prix');
    } finally {
      setSaving(false);
    }
  };

  const handleEditCancel = () => {
    setEditingProduct(null);
    setEditValue('');
  };

  const getMarginColor = (percent: number | null) => {
    if (percent === null) return 'text-gray-400 bg-gray-50';
    if (percent >= 40) return 'text-green-600 bg-green-50';
    if (percent >= 25) return 'text-blue-600 bg-blue-50';
    if (percent >= 10) return 'text-orange-600 bg-orange-50';
    return 'text-red-600 bg-red-50';
  };

  return (
    <div className="space-y-6">
      {/* KPIs Globales — marge sur HT, hors consignes/retours */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="CA HT"
          value={analysis ? formatCurrency(analysis.total_revenue_cents) : '-'}
          subtitle={analysis ? `TTC ${formatCurrency(analysis.total_revenue_ttc_cents)}` : undefined}
          icon="💰"
          color="blue"
          loading={loading}
        />
        <KPICard
          title="TVA collectée"
          value={analysis ? formatCurrency(analysis.total_vat_cents) : '-'}
          icon="🏛️"
          color="orange"
          loading={loading}
        />
        <KPICard
          title="Coût d'achat HT"
          value={analysis ? formatCurrency(analysis.total_cost_cents) : '-'}
          subtitle={analysis ? `sur ${formatCurrency(analysis.covered_revenue_cents)} de CA couvert` : undefined}
          icon="📦"
          color="orange"
          loading={loading}
        />
        <KPICard
          title="Marge brute (HT)"
          value={analysis ? formatCurrency(analysis.gross_margin_cents) : '-'}
          subtitle={analysis ? `${analysis.gross_margin_percent}% — couverture ${analysis.coverage_percent}%` : undefined}
          icon="📈"
          color={analysis && analysis.gross_margin_percent >= 25 ? 'green' : 'orange'}
          loading={loading}
        />
      </div>
      {analysis && analysis.coverage_percent < 100 && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 -mt-2">
          ⚠️ Marge calculée sur <strong>{analysis.coverage_percent}%</strong> du CA HT : seules les ventes dont le lot a un
          prix d'achat renseigné (ou issues de la production propre) sont comptées. Le reste est <strong>exclu</strong> du
          taux de marge, pas compté à coût zéro.
        </div>
      )}
      <p className="text-xs text-gray-500 -mt-2">
        Marge brute = <strong>CA HT couvert − coût d'achat HT</strong>, <strong>hors consignes et retours</strong>. Le CA
        affiché ailleurs (onglet Ventes) est le total encaissé <strong>TTC</strong>. Coût = prix d'achat du{' '}
        <strong>lot réellement décrémenté</strong> à chaque vente (comme l'onglet Marges revente, qui ajoute en plus la
        démarque par lot).
      </p>

      {/* Courbe des marges dans le temps : revente + production propre + total. */}
      <CourbeMarges from={from} to={to} />

      {/* Marges par catégorie */}
      {analysis && analysis.by_category.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-lg font-semibold mb-4">Marges par catégorie</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {analysis.by_category.map((cat) => (
              <div key={cat.category_id} className="border rounded-lg p-3">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium">{cat.category_name}</span>
                  <span className={`px-2 py-1 rounded text-sm font-semibold ${getMarginColor(cat.coverage_percent > 0 ? cat.margin_percent : null)}`}>
                    {cat.coverage_percent > 0 ? `${cat.margin_percent}%` : '—'}
                  </span>
                </div>
                <div className="text-sm text-gray-600">
                  <div className="flex justify-between">
                    <span>CA HT:</span>
                    <span>{formatCurrency(cat.revenue_cents)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Coût HT:</span>
                    <span>{formatCurrency(cat.cost_cents)}</span>
                  </div>
                  <div className="flex justify-between font-medium text-gray-900">
                    <span>Marge:</span>
                    <span>{cat.coverage_percent > 0 ? formatCurrency(cat.margin_cents) : '—'}</span>
                  </div>
                  {cat.coverage_percent < 100 && (
                    <div className="flex justify-between text-xs text-amber-700 mt-1">
                      <span>Couverture:</span>
                      <span>{cat.coverage_percent}%</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tableau des produits */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-4 py-3 border-b flex justify-between items-center">
          <h3 className="text-lg font-semibold">Marges par produit</h3>
          <span className="text-sm text-gray-500">{products.length} produits</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Produit</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Catégorie</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Prix Vente</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Prix Achat</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Marge Unit.</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">% Marge</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Qté Vendue</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Marge Totale</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {products.map((product) => (
                <tr key={product.product_id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900 truncate max-w-[200px]" title={product.product_name}>
                      {product.product_name}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{product.category_name}</td>
                  <td className="px-4 py-3 text-right text-sm">{formatCurrency(product.sale_price_cents)}</td>
                  <td className="px-4 py-3 text-right">
                    {editingProduct === product.product_id ? (
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="w-20 px-2 py-1 border rounded text-right text-sm"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleEditSave(product.product_id);
                          if (e.key === 'Escape') handleEditCancel();
                        }}
                      />
                    ) : (
                      <span className="text-sm">{formatCurrency(product.purchase_price_cents)}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-sm">
                    {product.unit_margin_cents !== null ? formatCurrency(product.unit_margin_cents) : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span
                      className={`px-2 py-1 rounded text-xs font-semibold ${getMarginColor(product.unit_margin_percent)}`}
                      title={product.unit_margin_percent === null ? "Prix d'achat inconnu" : undefined}
                    >
                      {product.unit_margin_percent !== null ? `${product.unit_margin_percent}%` : '?'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right text-sm">{product.quantity_sold}</td>
                  <td className="px-4 py-3 text-right text-sm font-medium">
                    {product.total_margin_cents !== null ? (
                      <span title={product.coverage_percent < 100 ? `Sur ${product.coverage_percent}% des unités vendues (coût connu)` : undefined}>
                        {formatCurrency(product.total_margin_cents)}
                        {product.coverage_percent < 100 && (
                          <span className="text-xs text-amber-600 ml-1">({product.coverage_percent}%)</span>
                        )}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {editingProduct === product.product_id ? (
                      <div className="flex justify-center gap-1">
                        <button
                          onClick={() => handleEditSave(product.product_id)}
                          disabled={saving}
                          className="px-2 py-1 bg-green-500 text-white rounded text-xs hover:bg-green-600 disabled:opacity-50"
                        >
                          {saving ? '...' : 'OK'}
                        </button>
                        <button
                          onClick={handleEditCancel}
                          className="px-2 py-1 bg-gray-300 text-gray-700 rounded text-xs hover:bg-gray-400"
                        >
                          X
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleEditStart(product)}
                        className="px-2 py-1 text-blue-600 hover:bg-blue-50 rounded text-xs"
                        title="Modifier le prix d'achat"
                      >
                        Modifier
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {products.length === 0 && !loading && (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                    Aucun produit trouvé pour cette période
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ============ INVENTORY TAB ============
function InventoryTab({
  overview,
  loading,
  formatCurrency,
}: {
  overview: InventoryOverview | null;
  loading: boolean;
  formatCurrency: (cents: number) => string;
}) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Valeur du stock"
          value={overview ? formatCurrency(overview.total_stock_value_cents) : '-'}
          subtitle="Au prix d'achat"
          icon="📦"
          color="blue"
          loading={loading}
        />
        <KPICard
          title="Unités en stock"
          value={overview?.total_stock_units ?? '-'}
          icon="🔢"
          color="green"
          loading={loading}
        />
        <KPICard
          title="Alertes stock bas"
          value={overview?.low_stock_alerts ?? '-'}
          icon="⚠️"
          color={overview && overview.low_stock_alerts > 0 ? 'orange' : 'green'}
          loading={loading}
        />
        <KPICard
          title="DLC proches"
          value={overview?.expiring_soon ?? '-'}
          subtitle="< 7 jours"
          icon="📅"
          color={overview && overview.expiring_soon > 0 ? 'red' : 'green'}
          loading={loading}
        />
      </div>

      {/* Placeholder for future charts */}
      <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
        <p className="text-xl mb-2">📊</p>
        <p>Graphiques d'inventaire détaillés à venir</p>
        <p className="text-sm">Évolution des stocks, valeur par catégorie, historique des pertes</p>
      </div>
    </div>
  );
}

// ============ CUSTOMERS TAB ============
function CustomersTab({
  overview,
  loading,
}: {
  overview: CustomerOverview | null;
  loading: boolean;
}) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Total clients"
          value={overview?.total_customers ?? '-'}
          icon="👥"
          color="blue"
          loading={loading}
        />
        <KPICard
          title="Clients actifs"
          value={overview?.active_customers_30d ?? '-'}
          subtitle="Achat < 30 jours"
          icon="✅"
          color="green"
          loading={loading}
        />
        <KPICard
          title="Nouveaux ce mois"
          value={overview?.new_customers_month ?? '-'}
          icon="🆕"
          color="purple"
          loading={loading}
        />
        <KPICard
          title="Taux de rétention"
          value={overview ? `${overview.retention_rate_percent}%` : '-'}
          icon="🔄"
          color="orange"
          loading={loading}
        />
      </div>

      {/* Placeholder for future charts */}
      <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
        <p className="text-xl mb-2">📊</p>
        <p>Segmentation RFM et analyse clients à venir</p>
        <p className="text-sm">Champions, Loyaux, À risque, Perdus</p>
      </div>
    </div>
  );
}

// ============ ECOMMERCE TAB ============
function EcommerceTab({
  overview,
  byPeriod,
  topProducts,
  categories,
  loading,
  formatCurrency,
}: {
  overview: EcomOverview | null;
  byPeriod: EcomByPeriod[];
  topProducts: EcomTopProduct[];
  categories: EcomCategoryBreakdown[];
  loading: boolean;
  formatCurrency: (cents: number) => string;
}) {
  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="CA TTC"
          value={overview ? formatCurrency(overview.total_ca_ttc_cents) : '-'}
          trend={overview?.comparison?.ca_variation_percent}
          trendLabel="vs période précédente"
          icon="💰"
          color="blue"
          loading={loading}
        />
        <KPICard
          title="Commandes"
          value={overview?.order_count ?? '-'}
          trend={overview?.comparison?.order_variation_percent}
          trendLabel="vs période précédente"
          icon="📦"
          color="green"
          loading={loading}
        />
        <KPICard
          title="Panier moyen"
          value={overview ? formatCurrency(overview.average_basket_cents) : '-'}
          trend={overview?.comparison?.basket_variation_percent}
          trendLabel="vs période précédente"
          icon="🛒"
          color="orange"
          loading={loading}
        />
        <KPICard
          title="Taux annulation"
          value={overview ? `${overview.cancellation_rate_percent}%` : '-'}
          icon="❌"
          color={overview && overview.cancellation_rate_percent > 10 ? 'red' : 'green'}
          loading={loading}
        />
      </div>

      {/* Secondary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
        <KPICard
          title="CA HT"
          value={overview ? formatCurrency(overview.total_ca_ht_cents) : '-'}
          icon="📊"
          color="blue"
          loading={loading}
        />
        <KPICard
          title="Livraisons"
          value={overview?.delivery_count ?? '-'}
          subtitle={overview ? `+ ${overview.pickup_count} retraits` : undefined}
          icon="🚚"
          color="purple"
          loading={loading}
        />
        <KPICard
          title="TVA collectée"
          value={overview ? formatCurrency(overview.total_tax_cents) : '-'}
          icon="🏛️"
          color="orange"
          loading={loading}
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Evolution CA */}
        <ChartCard title="Évolution CA e-commerce" subtitle="Par jour" loading={loading}>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={byPeriod}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="period"
                tickFormatter={(value) => format(new Date(value), 'dd/MM')}
                fontSize={12}
              />
              <YAxis
                tickFormatter={(value) => `${(value / 100).toFixed(0)}€`}
                fontSize={12}
              />
              <Tooltip
                formatter={(value, name) => [
                  `${(Number(value) / 100).toFixed(2)}€`,
                  name === 'ca_ttc_cents' ? 'CA TTC' : 'CA HT',
                ]}
                labelFormatter={(label) => format(new Date(String(label)), 'dd/MM/yyyy')}
              />
              <Line type="monotone" dataKey="ca_ttc_cents" stroke="#3B82F6" strokeWidth={2} dot={false} name="CA TTC" />
              <Line type="monotone" dataKey="ca_ht_cents" stroke="#10B981" strokeWidth={1} dot={false} name="CA HT" strokeDasharray="5 5" />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Répartition par catégorie */}
        <ChartCard title="CA par catégorie" loading={loading}>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={categories as any[]}
                dataKey="ca_cents"
                nameKey="category_name"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label={({ name, payload }: any) =>
                  `${name} (${payload?.percent_of_total || 0}%)`
                }
                labelLine={false}
              >
                {categories.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => [`${(Number(value) / 100).toFixed(2)}€`, 'CA']} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Top Products */}
      <ChartCard title="Top 10 Produits E-commerce" subtitle="Par chiffre d'affaires" loading={loading}>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={topProducts} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" tickFormatter={(value) => `${(value / 100).toFixed(0)}€`} />
            <YAxis
              type="category"
              dataKey="product_name"
              width={150}
              fontSize={12}
              tickFormatter={(value) => value.length > 20 ? value.substring(0, 20) + '...' : value}
            />
            <Tooltip
              formatter={(value) => [`${(Number(value) / 100).toFixed(2)}€`, 'CA']}
            />
            <Bar dataKey="total_ca_cents" fill="#8B5CF6" />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Commandes par période */}
      <ChartCard title="Commandes par jour" loading={loading}>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={byPeriod}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="period"
              tickFormatter={(value) => format(new Date(value), 'dd/MM')}
              fontSize={12}
            />
            <YAxis fontSize={12} />
            <Tooltip
              formatter={(value) => [value, 'Commandes']}
              labelFormatter={(label) => format(new Date(String(label)), 'dd/MM/yyyy')}
            />
            <Bar dataKey="order_count" fill="#10B981" />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}

// ============ OPERATIONAL TAB ============
function OperationalTab({
  kpis,
  loading,
  formatCurrency,
}: {
  kpis: OperationalKPIs | null;
  loading: boolean;
  formatCurrency: (cents: number) => string;
}) {
  return (
    <div className="space-y-6">
      {/* Today's Progress */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Progression du jour</h3>
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <div className="flex justify-between text-sm mb-1">
              <span>CA du jour</span>
              <span className="font-medium">
                {kpis ? formatCurrency(kpis.today_ca_cents) : '-'} /{' '}
                {kpis ? formatCurrency(kpis.today_target_cents) : '-'}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-4">
              <div
                className={`h-4 rounded-full transition-all ${
                  (kpis?.today_progress_percent ?? 0) >= 100
                    ? 'bg-green-500'
                    : (kpis?.today_progress_percent ?? 0) >= 50
                    ? 'bg-blue-500'
                    : 'bg-orange-500'
                }`}
                style={{ width: `${Math.min(kpis?.today_progress_percent ?? 0, 100)}%` }}
              />
            </div>
          </div>
          <div className="text-2xl font-bold text-blue-600">
            {kpis?.today_progress_percent ?? 0}%
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <KPICard
          title="CA aujourd'hui"
          value={kpis ? formatCurrency(kpis.today_ca_cents) : '-'}
          icon="💰"
          color="blue"
          loading={loading}
        />
        <KPICard
          title="Transactions"
          value={kpis?.transactions_today ?? '-'}
          icon="🧾"
          color="green"
          loading={loading}
        />
        <KPICard
          title="Alertes actives"
          value={kpis?.active_alerts.reduce((sum, a) => sum + a.count, 0) ?? 0}
          icon="⚠️"
          color={kpis && kpis.active_alerts.length > 0 ? 'orange' : 'green'}
          loading={loading}
        />
      </div>

      {/* Alerts */}
      {kpis && kpis.active_alerts.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-lg font-semibold mb-3">Alertes actives</h3>
          <div className="space-y-2">
            {kpis.active_alerts.map((alert, index) => (
              <div
                key={index}
                className={`flex items-center gap-3 p-3 rounded-lg ${
                  alert.type === 'expiring'
                    ? 'bg-red-50 text-red-700'
                    : alert.type === 'low_stock'
                    ? 'bg-orange-50 text-orange-700'
                    : 'bg-yellow-50 text-yellow-700'
                }`}
              >
                <span className="text-xl">
                  {alert.type === 'expiring' ? '📅' : alert.type === 'low_stock' ? '📦' : '⚠️'}
                </span>
                <span>{alert.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
